import { Category } from './category.types';
import { Producto } from './product.types';

/** Estados de línea en `carrito_detalle`. */
export const CartDetalleEstado = {
  EN_CARRITO: 1,
  GUARDADO_PARA_DESPUES: 2,
} as const;

/** Tipos de alerta emitidos por el API en cada línea del carrito. */
export const CartItemAlertaTipo = {
  PRECIO_ACTUALIZADO: 'PRECIO_ACTUALIZADO',
  PRECIO_INCREMENTADO: 'PRECIO_INCREMENTADO',
  PRECIO_DISMINUIDO: 'PRECIO_DISMINUIDO',
  INVENTARIO_AJUSTADO: 'INVENTARIO_AJUSTADO',
  SIN_INVENTARIO: 'SIN_INVENTARIO',
} as const;

export type CartItemAlertaTipoValue =
  (typeof CartItemAlertaTipo)[keyof typeof CartItemAlertaTipo];

export interface CartItemAlerta {
  tipo: CartItemAlertaTipoValue | string;
  idDetalle: number;
  codigoProducto?: string | null;
  mensaje: string;
  precioAnterior?: string | number | null;
  precioActual?: number;
  diferencia?: number;
  porcentaje?: number | null;
  cantidadSolicitada?: number;
  cantidadDisponible?: number;
  sucursal?: string;
}

export interface Cart {
  id: number;
  idUsuarioDigital: number;
  idVehiculoSeleccionado?: number | null;
  sucursalSeleccionada?: string | null;
  nombreContacto?: string | null;
  telefonoContacto?: string | null;
  fechaCreacion: Date;
  idEstado: number;
  idCotizacion?: number | null;
  items: CartItem[];
}

export interface CartItem {
  id: number;
  idCarrito: number;
  codigoProducto?: string | null;
  idGrupo?: number | null;
  cantidad: number;
  notas?: string | null;
  fechaCreacion: Date;
  /** 1 = en carrito, 2 = guardado para después. Ausente en carrito guest local. */
  idEstado?: number | null;
  estado?: string | null;
  precioGuardado?: number | string | null;
  producto?: Producto;
  grupo?: Category;
  selectedDelivery?: string;
  alertas?: CartItemAlerta[];
}

export interface CreateCartPayload {
  idUsuarioDigital: number;
  idVehiculoSeleccionado?: number | null;
  sucursalSeleccionada?: string | null;
  nombreContacto?: string | null;
}

export interface AddItemToCartPayloadBackend {
  /** SKU del producto. Mutuamente exclusivo con idGrupo a nivel de identidad. */
  codigoProducto?: string;
  /** ID de grupo/categoría (website sin SKU). */
  idGrupo?: number;
  cantidad: number;
  /** 1 = en carrito, 2 = guardado para después. */
  idEstado?: number;
  notas?: string | null;
}

export interface UpdateCartItemPayload {
  idDetalle: number;
  cantidad: number;
  notas?: string | null;
}

export interface AddItemToCartPayload {
  categoria?: Category;
  producto?: Producto;
  cantidad?: number;
}

export function isCartItemEnCarrito(item: CartItem): boolean {
  return (
    item.idEstado == null || item.idEstado === CartDetalleEstado.EN_CARRITO
  );
}

export function isCartItemGuardadoParaDespues(item: CartItem): boolean {
  return item.idEstado === CartDetalleEstado.GUARDADO_PARA_DESPUES;
}

/**
 * Precio a mostrar en filas del carrito (web): precio + impuesto.
 * La lógica de negocio debe usar `precio` e `impuesto` por separado.
 */
export function precioMostrarProducto(
  producto?: Pick<Producto, 'precio' | 'impuesto'> | null
): number {
  const precio = Number(producto?.precio ?? 0);
  const impuesto = Number(producto?.impuesto ?? 0);
  const base = Number.isFinite(precio) ? precio : 0;
  const tax = Number.isFinite(impuesto) ? impuesto : 0;
  return base + tax;
}

/**
 * Imagen a mostrar solo en carrito web:
 * `imagenes.thumb` → `imagenes_tienda` → url de grupo → placeholder.
 */
export function imagenMostrarProductoCarrito(
  producto?: Pick<Producto, 'imagenes' | 'imagenes_tienda'> | null,
  urlImagenGrupo?: string | null
): string {
  const thumb = producto?.imagenes?.thumb?.[0]?.trim();
  if (thumb) {
    return thumb;
  }

  for (const img of producto?.imagenes_tienda ?? []) {
    const full = img?.ubicacion_foto?.trim();
    if (full) {
      return full;
    }
    const menor = img?.ubicacion_foto_menor_calidad?.trim();
    if (menor) {
      return menor;
    }
  }

  const grupo = urlImagenGrupo?.trim();
  if (grupo) {
    return grupo;
  }

  return '/placeholder.svg';
}
